import queryString from 'query-string';
import get from 'lodash/get';

import fetchQuery from 'rebook/apps/utils/fetchQuery';
import getPageNumberString from 'rebook/apps/utils/text/getPageNumberString';
import {
  CATALOG_NICHE_BOOK_LIST_SET_DATA, CATALOG_NICHE_BOOK_LIST_RESET_DATA,
} from 'rebook/apps/catalog/constants';
import * as SeoActions from 'rebook/apps/base/actions/SeoActions';
import { Niche } from 'rebook/apps/catalog/models';


export function fetchInitialData(match, location = {}, paginateBy) {
  return async (dispatch, getState) => {
    const { ssr, nicheBookList: currentState } = getState();
    const pageNumber = parseInt(get(ssr, 'query.page', 1), 10);
    const orderBy = get(ssr, 'query.o', 'popular');
    const subscriptionId = get(ssr, 'query.s', null);
    const bookType = get(ssr, 'query.t', null);

    const { nicheSlug } = match.params;

    let newState = {
      match,
      nicheSlug,
      paginateBy,
      pageNumber,
      orderBy,
      subscriptionId,
      bookType,
    };

    if (location && location.state) {
      newState = {
        ...newState,
        ...location.state,
      };
    }
    if (currentState.genre) {
      newState = {
        ...newState,
        ...currentState,
      };
    }

    dispatch(setData(newState));

    if (!newState.niche) {
      await dispatch(fetchDetail(match));
    }

    dispatch(setSeoMeta(match));

    if (!newState.books || currentState.pageNumber !== pageNumber) {
      await dispatch(fetchBookList(newState, match));
    }
  };
}

export function fetchDetail(match) {
  return async (dispatch, getState) => {
    const { ssr } = getState();
    const { nicheSlug } = match.params;
    const url = `/api/catalog/niches/s-${nicheSlug}/`;

    return (
      fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
        .then(({ data }) => {
          dispatch(setData({
            niche: data,
          }));
          return data;
        })
        .catch(({ data, resp, error }) => {
          Raven.captureMessage('failed to load niche for niche book list due to server error', {
            level: 'error',
            extra: {
              status: resp.status,
              response: data,
              error,
            },
          });
          throw new Error('failed to load niche for niche book list due to server error');
        })
    );
  };
}

export function fetchBookList(params = {}, match) {
  return async (dispatch, getState) => {
    const catalogBookType = get(match, 'params.bookType', '');
    const areBooksFree = catalogBookType === 'free';
    const areBooksAudio = catalogBookType === 'audio';
    const { ssr, nicheBookList } = getState();
    const mergedState = Object.assign({}, nicheBookList, params);
    const { niche, paginateBy, orderBy, subscriptionId, bookType } = mergedState;

    let pageNumber = mergedState.pageNumber;
    if (!params.pageNumber &&
      (nicheBookList.subscriptionId !== mergedState.subscriptionId)
      || (nicheBookList.bookType !== mergedState.bookType)
    ) {
      pageNumber = 1;
    }

    if (!niche) {
      return null;
    }

    let queryParams = {
      niches: niche.id,
      offset: (pageNumber - 1) * paginateBy,
      limit: paginateBy,
      o: orderBy,
      author_weight__gte: 0,
      ...(subscriptionId !== null && {
        subscription_id__in: subscriptionId,
      }),
      ...(bookType !== null && {
        type: bookType,
      }),
    };

    if (areBooksFree) {
      queryParams['subscription_id__in'] = 0;
    }

    if (bookType === 'audio' || areBooksAudio) {
      queryParams['type'] = 'audio';
    }
    const qs = queryString.stringify(queryParams, { encode: false });
    const url = `/api/books/?${qs}`;
    const pendingState = {
      // clear the book list before the fetch
      books: null,
      // optimistically update the UI
      pageNumber: pageNumber,
      orderBy,
      lastPageNumber: 0,
      ...(subscriptionId !== null && { subscriptionId }),
      ...(bookType !== null && { bookType }),
    };
    dispatch(setData(pendingState));
    return (
      fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
        .then(
          ({ data }) => {
            const lastPageNumber = Math.ceil(data.meta.total_count / paginateBy);
            dispatch(setData({
              books: data.objects,
              lastPageNumber: lastPageNumber,
              totalBookCount: data.meta.total_count,
              subscriptionId,
              bookType,
            }));
          },
        )
        .catch(
          ({ data, resp, error }) => {
            Raven.captureMessage('failed to load niche book list due to server error', {
              level: 'error',
              extra: {
                status: resp.status,
                response: data,
                error,
              },
            });
            throw new Error('failed to load niche book list due to server error');
          },
        )
    );
  };
}

export function setSeoMeta(match) {
  return (dispatch, getState) => {
    const bookType = get(match, 'params.bookType', 'byDefault');
    const { niche: nicheObject, pageNumber } = getState().nicheBookList;
    const niche = new Niche(nicheObject);

    const seoMeta = {
      byDefault: {
        url: `https://${BASE_HOST}${niche.getUrl()}books/`,
        title: `Книги жанра «${niche.name}» читать онлайн на MyBook. ` +
          `Рейтинг и отзывы читателей в электронной библиотеке MyBook. ${getPageNumberString(pageNumber).trim()}`,
        description: `Читать онлайн ${niche.active_book_count} книг в жанре «${niche.name}».` +
          `Полный список популярных книг жанра «${niche.name}», рейтинг и отзывы ` +
          `читателей в электронной библиотеке MyBook.`,
      },
      free: {
        url: `https://${BASE_HOST}${niche.getUrl()}books/free/`,
        title: `«${niche.name}» — читать онлайн бесплатные книги в электронной библиотеке MyBook` +
          `${getPageNumberString(pageNumber).trim()}`,
        description: `Читайте бесплатно книги в жанре «${niche.name}» в электронной библиотеке MyBook.` +
          `Более ${niche.counters.free} бесплатных книг онлайн.`,
      },
      audio: {
        url: `https://${BASE_HOST}${niche.getUrl()}books/audio/`,
        title: `«${niche.name}»  — слушать аудиокниги онлайн на MyBook`,
        description: `Аудиокниги в жанре «${niche.name}» — слушайте новинки и бестселлеры жанра, ` +
          `популярных авторов и чтецов, делитесь рецензиями в электронной библиотеке MyBook.`,
      },
    };
    dispatch(SeoActions.setData({
      title: seoMeta[bookType].title,
      description: seoMeta[bookType].description,
      canonicalUrl: seoMeta[bookType].url,
      pageNumber,
    }));
  };
}

export function setData(payload) {
  return {
    type: CATALOG_NICHE_BOOK_LIST_SET_DATA,
    payload,
  };
}

export function resetData() {
  return (dispatch) => {
    dispatch({
      type: CATALOG_NICHE_BOOK_LIST_RESET_DATA,
    });
    dispatch(SeoActions.resetData());
  };
}
